import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Workflow as WorkflowIcon, Zap } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { createWorkflow, listWorkflows, type WorkflowSummary } from '../../lib/workflows-api'
import { TRIGGER_LABELS } from './shared'

export function WorkflowsList() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['workflows'],
    queryFn: listWorkflows,
  })

  const create = useMutation({
    mutationFn: () =>
      createWorkflow({
        name: 'Untitled workflow',
        enabled: false,
        trigger: { type: 'signup.created', config: {} },
        actions: [],
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      navigate(`/app/workflows/${created.id}`)
    },
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Workflows"
        description="Automate routine work: react to signups, list changes, and form submissions."
        actions={
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? <Spinner /> : <Plus />}
            New workflow
          </Button>
        }
      />

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading workflows…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load workflows.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <div className="grid size-12 place-items-center rounded-full bg-card text-secondary">
              <WorkflowIcon className="size-6" />
            </div>
            <p className="max-w-sm text-sm text-muted">
              No workflows yet. Create one to add people to a list when they sign up, notify a
              leader on a new form submission, and more.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Trigger</th>
                  <th className="px-5 py-3 font-semibold">Actions</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Last run</th>
                </tr>
              </thead>
              <tbody>
                {data.map((w: WorkflowSummary) => (
                  <tr
                    key={w.id}
                    tabIndex={0}
                    role="link"
                    onClick={() => navigate(`/app/workflows/${w.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/app/workflows/${w.id}`)
                      }
                    }}
                    className="cursor-pointer border-b border-border-soft outline-none transition-colors last:border-0 hover:bg-card-hover focus-visible:bg-card-hover"
                  >
                    <td className="px-5 py-3">
                      <div className="font-medium">{w.name}</div>
                      {w.description && (
                        <div className="truncate text-xs text-dim">{w.description}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-muted">
                      <span className="inline-flex items-center gap-1.5">
                        <Zap className="size-3.5 text-secondary" />
                        {TRIGGER_LABELS[w.trigger.type] ?? w.trigger.type}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-muted">{w.actionCount}</td>
                    <td className="px-5 py-3">
                      <Badge variant={w.enabled ? 'success' : 'neutral'}>
                        {w.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-muted">
                      {w.lastRunAt ? new Date(w.lastRunAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
